import React from 'react';
import { MenuProps as MuiMenuProps } from '@mui/material';

// Context for managing dropdown state
interface DropdownContextValue {
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  handleOpen: (event: React.MouseEvent<HTMLElement>) => void;
  handleClose: () => void;
}

const DropdownContext = React.createContext<DropdownContextValue | undefined>(undefined);

export const useDropdownContext = () => {
  const context = React.useContext(DropdownContext);
  if (!context) {
    throw new Error('Dropdown components must be used within a Dropdown');
  }
  return context;
};

export interface DropdownProps extends Omit<MuiMenuProps, 'open'> {
  /** Children elements (typically DropdownButton and DropdownMenu) */
  children?: React.ReactNode;
}

/**
 * Dropdown component provides a menu overlay triggered by a button.
 *
 * Dropdown component provides a menu overlay triggered by a button.\n\nThis component maintains
 * API compatibility with the original pounce Dropdown. It uses MUI's Menu component internally but
 * manages state through the context pattern to match the original API.
 *
 * @example
 * <Dropdown>
 *   <DropdownButton>Actions</DropdownButton>
 *   <DropdownMenu>
 *     <DropdownItem onSelect={() => {}}>Edit</DropdownItem>
 *     <DropdownItem onSelect={() => {}}>Delete</DropdownItem>
 *   </DropdownMenu>
 * </Dropdown>
 */
export const Dropdown: React.FC<DropdownProps> = ({ children }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <DropdownContext.Provider value={{ anchorEl, isOpen, handleOpen, handleClose }}>
      {children}
    </DropdownContext.Provider>
  );
};
